<template>
  <div class="row mb-5">
    <div class="col-md-4">
      <form method="post" @submit.prevent="getObjects()">
        <div class="card">
          <div class="card-body">
            <div class="mb-3">
              <v-text-input
                v-model="searchQuery"
                size="lg"
                placeholder="sentence.search_dashboard"
              />
            </div>

            <div class="card card-body bg-light" v-if="metaData">
              <div>
                <h5 class="m-0">{{ $t('word.published') }}</h5>
                <div
                  v-for="(year, index) in metaData.years"
                  :key="year.key"
                  v-show="showAllYears || (index < filterListSize)"
                >
                  <label class="m-0">
                    <input
                      type="checkbox"
                      name="years"
                      :id="`year_${year.key}`"
                      :value="year.key"
                      v-model="selectedYears"
                    />
                    {{ year.value }}
                  </label>
                </div>
                <div
                  class="d-flex justify-content-center"
                  v-if="metaData.years.length > filterListSize"
                >
                  <v-button
                    :text="`phrase.show_${showAllYears ? 'less' : 'more'}`"
                    :icon="`chevron-${showAllYears ? 'up' : 'down'}`"
                    size="sm"
                    style-type="link"
                    @click="showAllYears = !showAllYears"
                  />
                </div>
              </div>
              <hr />
              <div>
                <h5 class="m-0">{{ $t('word.industry') }}</h5>
                <div
                  v-for="(industry, index) in metaData.industries"
                  :key="industry.key"
                  v-show="showAllIndustries || (index < filterListSize)"
                >
                  <label class="m-0">
                    <input
                      type="checkbox"
                      name="industries"
                      :id="`industry_${industry.key}`"
                      :value="industry.key"
                      v-model="selectedIndustries"
                    />
                    {{ industry.value }}
                  </label>
                </div>
                <hr />
                <div
                  class="d-flex justify-content-center"
                  v-if="metaData.industries.length > filterListSize"
                >
                  <v-button
                    :text="`phrase.show_${showAllIndustries ? 'less' : 'more'}`"
                    :icon="`chevron-${showAllIndustries ? 'up' : 'down'}`"
                    size="sm"
                    style-type="link"
                    @click="showAllIndustries = !showAllIndustries"
                  />
                </div>
              </div>
              <div class="mb-3">
                <h5 class="m-0">{{ $t('word.category') }}</h5>
                <div
                  v-for="(category, index) in metaData.categories"
                  :key="category.key"
                  v-show="showAllCategories || (index < filterListSize)"
                >
                  <label class="m-0">
                    <input
                      type="checkbox"
                      name="categories"
                      :id="`category_${category.key}`"
                      :value="category.key"
                      v-model="selectedCategories"
                    />
                    {{ category.value }}
                  </label>
                </div>
                <div
                  class="d-flex justify-content-center"
                  v-if="metaData.categories.length > filterListSize"
                >
                  <v-button
                    :text="`phrase.show_${showAllCategories ? 'less' : 'more'}`"
                    :icon="`chevron-${showAllCategories ? 'up' : 'down'}`"
                    size="sm"
                    style-type="link"
                    @click="showAllCategories = !showAllCategories"
                  />
                </div>
              </div>
            </div>
          </div>
          <div class="card-footer">
            <div class="d-flex justify-content-end">
              <v-button
                style-type="link"
                type="button"
                text="word.reset"
                @click="resetForm()"
              />
              <v-button
                type="submit"
                text="word.filter"
                :loader="{loading: loading}"
              />
            </div>
          </div>
        </div>
      </form>

    </div>
    <div class="col-md-8">
      <div class="card">
        <div class="card-header">
          <div class="d-flex justify-content-between">
            <div>
              <v-loader v-if="loading" :small="true" />
            </div>
            <h4 class="m-0" v-if="totalObjects > 0">
              {{ totalObjects }} {{ $t('word.commercials').toLowerCase() }}
            </h4>
          </div>
        </div>
        <div class="card-body" v-if="objectList && objectList.length > 0">
          <div class="row">
            <div
              v-for="research in objectList"
              :key="research.id"
              class="col-lg-6"
            >
              <v-reviewer-research-card
                :research="research"
              />
            </div>
          </div>

          <div class="d-flex justify-content-center">
            <v-button
              v-if="nextPageUrl"
              text="phrase.load_more"
              @click="getObjects(nextPageUrl, true)"
            />
          </div>
        </div>
        <div class="card card-body" v-else-if="!loading">
          <div class="alert alert-info m-0">
            {{ $t('phrase.no_commercials_found') }}
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Button from '@/common/components/Button.vue';
import TextInput from '@/common/components/inputs/TextInput.vue';
import { GenericListMixin } from '@/common/utils/mixins';
import api from '@/common/api';
import { setBreadcrumbs } from '@/common/utils/breadcrumbs';
import Loader from '@/common/components/Loader.vue';
import ReviewerResearchCard from '@/reviewer/components/ReviewerResearchCard.vue';

export default {
  name: 'CommercialResearchList',
  components: {
    'v-button': Button,
    'v-reviewer-research-card': ReviewerResearchCard,
    'v-text-input': TextInput,
    'v-loader': Loader,
  },
  methods: {
    getParams() {
      return {
        years: this.selectedYears.join(','),
        industries: this.selectedIndustries.join(','),
        categories: this.selectedCategories.join(','),
      };
    },
    resetForm() {
      this.selectedYears = [];
      this.reset();
    },
    async getCommercialMetaData() {
      const response = await api.get('public/commercials/meta-data/');
      this.metaData = response.data;
    },
  },
  data() {
    return {
      selectedCategories: [],
      selectedIndustries: [],
      selectedYears: [],
      showAllYears: false,
      showAllIndustries: false,
      showAllCategories: false,
      filterListSize: 5,
      apiPath: 'rev/researches/',
      metaData: null,
    };
  },
  mixins: [
    GenericListMixin,
  ],
  created() {
    setBreadcrumbs('commercialList');

    this.getCommercialMetaData();
  },
};
</script>
